import FilterListIcon from "@mui/icons-material/FilterList";
import React, { FC, useEffect, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import { useSearchClientSavedFiltersMutation } from "../../../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../../../api/services/client/models";
import { ReactComponent as DeleteIcon } from "../../../../assets/images/delete-icon.svg";
import { ReactComponent as ResetAllIcon } from "../../../../assets/images/reset-all.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiButtonTertiary,
  MuiGrid,
  MuiMenuList,
  MuiOptionMenu,
} from "../../../../components";
import { CommonListModal } from "../../../../components/commonListModal/CommonListModal";
import { DeleteFilterModal } from "../../../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../../../components/searchFilters/saveFilter/SaveFilter";
import {
  MAX_SAVE_FILTERS_LIMIT,
  SAVED_SEARCH_PAGE_NAME,
} from "../../../../helpers/constants";
import {
  isEmpty,
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { LabelWithIconText, Separator } from "../../../../styles/common.style";
import { FilterProps } from "../../../clientSearch/modelsAndSchemas/Models";
import {
  programSearchProgramDefaultValues,
  useProgramSearchData,
} from "../../ProgramSearch";
import {
  ProgramSearchProgramFormKey,
  ProgramSearchProgramFormProps,
  searchTypeKey,
} from "../../modelsAndSchemas/models";
import { ProgramSearchForm } from "../programSearchForm/ProgramSearchForm";
import { ProgramSearchFormMainWrapper } from "./ProgramSearchFormMain.style";

interface ProgramSearchProps extends FieldValues {
  pageId: string;
}

export const ProgramSearchFormMain: FC<ProgramSearchProps> = ({ pageId }) => {
  const { commonLabels } = useSiteCore();
  const { getValues, reset } = useFormContext();
  const { setShowMore, setSearchResultList, setIsSearchClick } =
    useProgramSearchData();
  const [showModal, setShowModal] = useState(false);
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [isOpenSaveFilterModal, setIsOpenSaveFilterModal] = useState(false);
  const [filtersData, setFiltersData] = useState<FilterProps[]>([]);
  const [showSavedFilter, setShowSavedFilter] = useState(false);
  const [filterToDeleteProgram, setFilterToDeleteProgram] =
    useState<MenuListParams>({
      id: "",
      name: "",
    });
  const [preloadFilter, setPreloadFilter] = useState(false);
  const [isMaxFilterReached, setIsMaxFilterReached] = useState(false);
  const [selectedSavedSearchPageName, setSelectedSavedSearchPageName] =
    useState(SAVED_SEARCH_PAGE_NAME.INDIVIDUAL_SEARCH);
  const [preloadFilterValuesProgram, setPreloadFilterValuesProgram] = useState({
    id: "",
    name: "",
    filters: filtersData,
  });
  const [finalListOfFilters, setFinalListOfFilters] = useState<
    SaveSearchFilterParams[]
  >([]);
  const { isMobile } = useMediaQueries();
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const {
    data: listOfFiltersIndividuals,
    refetch: refetchListOfFiltersIndividuals,
    isFetching: isListOfFiltersIndividualsFetching,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "IndividualsSearch",
  });

  const {
    data: listOfFiltersProgram,
    refetch: refetchListOfFiltersProgram,
    isFetching: isListOfFiltersProgramFetching,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "ProgramSearch",
  });

  const {
    data: listOfFiltersGenericParticipant,
    refetch: refetchListOfFiltersGenericParticipant,
    isFetching: isListOfFiltersGenericParticipantFetching,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "GenericParticipantsSearch",
  });

  const {
    data: listOfFiltersNISParticipant,
    refetch: refetchListOfFiltersNISParticipant,
    isFetching: isListOfFiltersNISParticipantFetching,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "NisParticipantSearch",
  });

  useEffect(() => {
    if (getValues().searchType === searchTypeKey.programs) {
      setFinalListOfFilters([]);
      setFinalListOfFilters([...(listOfFiltersProgram || [])]);
    }
  }, [isListOfFiltersProgramFetching]);

  useEffect(() => {
    if (getValues().searchType === searchTypeKey.genericParticipant) {
      setFinalListOfFilters([]);
      setFinalListOfFilters([...(listOfFiltersGenericParticipant || [])]);
    }
  }, [isListOfFiltersGenericParticipantFetching]);

  useEffect(() => {
    if (getValues().searchType === searchTypeKey.nisParticipants) {
      setFinalListOfFilters([]);
      setFinalListOfFilters([...(listOfFiltersNISParticipant || [])]);
    }
  }, [isListOfFiltersNISParticipantFetching]);

  useEffect(() => {
    if (getValues().searchType === searchTypeKey.individuals) {
      setFinalListOfFilters([]);
      setFinalListOfFilters([...(listOfFiltersIndividuals || [])]);
    }
  }, [isListOfFiltersIndividualsFetching]);

  const resetFormValueBySearchType = (isRadioToggleChange: boolean) => {
    const { resetForm, searchType } = getValues();
    reset({
      ...programSearchProgramDefaultValues,
      [ProgramSearchProgramFormKey.resetForm]: !resetForm,
      ...(isRadioToggleChange && {
        [ProgramSearchProgramFormKey.searchType]: searchType,
      }),
    });

    const pageNameMap: Record<string, number> = {
      [searchTypeKey.individuals]: SAVED_SEARCH_PAGE_NAME.INDIVIDUAL_SEARCH,
      [searchTypeKey.programs]: SAVED_SEARCH_PAGE_NAME.PROGRAM_SEARCH,
      [searchTypeKey.genericParticipant]:
        SAVED_SEARCH_PAGE_NAME.GENERIC_PARTICIPANT_SEARCH,
      [searchTypeKey.nisParticipants]:
        SAVED_SEARCH_PAGE_NAME.NIS_PARTICIPANT_SEARCH,
    };

    const targetSearchType =
      !isRadioToggleChange || searchType === searchTypeKey.individuals
        ? searchTypeKey.individuals
        : searchType;

    filterListRefetchType(targetSearchType);
    setSelectedSavedSearchPageName(pageNameMap[targetSearchType]);

    setShowMore(false);
    removeSavedFilters();

    // resetting table results on reset and radio button change
    setSearchResultList([]);
    setIsSearchClick(false);
  };

  const handleSetFilterModal = () => {
    setIsOpenSaveFilterModal(!isOpenSaveFilterModal);
  };

  const handleClose = () => {
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleModalClose = () => {
    if (showModal) {
      setShowModal(false);
    }
  };

  const removeSavedFilters = () => {
    setPreloadFilterValuesProgram({ id: "", name: "", filters: [] });
    setPreloadFilter(false);
  };

  const handleFilterRowClickProgram = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    setPreloadFilter(true);
    const itemFilterProgramSearch = item as SaveSearchFilterParams;
    setPreloadFilterValuesProgram({
      id: itemFilterProgramSearch.id ?? "",
      name: itemFilterProgramSearch.name ?? "",
      filters: mapFiltersToFormValues(
        itemFilterProgramSearch.filters,
        {
          ...programSearchProgramDefaultValues,
          searchType: getValues().searchType,
        },
        ProgramSearchProgramFormKey,
      ),
    });
    reset(
      mapFiltersToFormValues(
        itemFilterProgramSearch.filters,
        {
          ...programSearchProgramDefaultValues,
          searchType: getValues().searchType,
        },
        ProgramSearchProgramFormKey,
      ),
    );
    if (showSavedFilter === true) {
      setShowSavedFilter(false);
    }
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: MenuListParams,
  ) => {
    event.stopPropagation();
    if (preloadFilterValuesProgram.name === item.name) {
      removeSavedFilters();
    }

    setFilterToDeleteProgram({ id: item?.id, name: item?.name });
    if (showModal === false) {
      setShowModal(true);
    }
  };

  const handleSavedFilter = () => {
    if (showSavedFilter === false) {
      setShowSavedFilter(true);
    }
  };

  const closeSaveFilterModal = () => {
    setIsOpenSaveFilterModal(false);
  };

  const handlePrimaryButtonClick = () => {
    setShowModal(false);
  };

  const handleMultipleDeleteModalClose = () => {
    if (showMultipleDeleteModal) {
      setShowMultipleDeleteModal(false);
    }
  };

  const handleMultipleDeletePrimaryButtonClick = () => {
    setShowMultipleDeleteModal(false);
  };

  const handleSaveFilterModal = () => {
    if (
      finalListOfFilters &&
      finalListOfFilters.length >= MAX_SAVE_FILTERS_LIMIT &&
      !preloadFilter
    ) {
      if (showMultipleDeleteModal === false) {
        setShowMultipleDeleteModal(true);
      }
    } else {
      handleSetFilterModal();
    }
    const values = getValues();

    const filters: {
      fieldName: keyof ProgramSearchProgramFormProps;
      operatorIdKey?: keyof ProgramSearchProgramFormProps;
    }[] = [
      {
        fieldName: ProgramSearchProgramFormKey.participantFirstName,
        operatorIdKey: ProgramSearchProgramFormKey.participantFirstNameFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.participantLastName,
        operatorIdKey: ProgramSearchProgramFormKey.participantLastNameFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.keyNumber,
        operatorIdKey: ProgramSearchProgramFormKey.keyNumberFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.participantEmail,
        operatorIdKey: ProgramSearchProgramFormKey.participantEmailFilter,
      },

      {
        fieldName: ProgramSearchProgramFormKey.clientName,
        operatorIdKey: ProgramSearchProgramFormKey.clientNameFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.clientManagedStatus,
      },
      {
        fieldName: ProgramSearchProgramFormKey.programName,
      },
      {
        fieldName: ProgramSearchProgramFormKey.programStatus,
      },
      {
        fieldName: ProgramSearchProgramFormKey.programStartMonthAndYear,
      },
      {
        fieldName: ProgramSearchProgramFormKey.billingContactLastName,
        operatorIdKey: ProgramSearchProgramFormKey.billingContactLastNameFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.hrContactLastName,
        operatorIdKey: ProgramSearchProgramFormKey.hrContactLastNameFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.projectManager,
      },
      {
        fieldName: ProgramSearchProgramFormKey.lineOfBusiness,
      },
      {
        fieldName: ProgramSearchProgramFormKey.solutionCategory,
      },
      {
        fieldName: ProgramSearchProgramFormKey.clientRelationshipOwner,
      },
      {
        fieldName: ProgramSearchProgramFormKey.mobileISOCode,
      },
      {
        fieldName: ProgramSearchProgramFormKey.mobileCountryCode,
      },
      {
        fieldName: ProgramSearchProgramFormKey.participantMobileNumber,
      },
      {
        fieldName: ProgramSearchProgramFormKey.genericAccountName,
        operatorIdKey: ProgramSearchProgramFormKey.genericAccountNameFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.genericRegistrationCode,
        operatorIdKey:
          ProgramSearchProgramFormKey.genericRegistrationCodeFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.nextView,
      },
      {
        fieldName: ProgramSearchProgramFormKey.genericAccountStatus,
      },

      {
        fieldName: ProgramSearchProgramFormKey.nextProgramClass,
      },
      {
        fieldName: ProgramSearchProgramFormKey.participantStatus,
      },
      {
        fieldName: ProgramSearchProgramFormKey.purpose,
      },
      {
        fieldName: ProgramSearchProgramFormKey.country,
      },
      {
        fieldName: ProgramSearchProgramFormKey.requestedBy,
      },
      {
        fieldName: ProgramSearchProgramFormKey.deliveryOffice,
      },
      {
        fieldName: ProgramSearchProgramFormKey.nisId,
        operatorIdKey: ProgramSearchProgramFormKey.nisIdFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.clientEmployer,
        operatorIdKey: ProgramSearchProgramFormKey.clientEmployerFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.nisStatus,
      },
      {
        fieldName: ProgramSearchProgramFormKey.hrContact,
        operatorIdKey: ProgramSearchProgramFormKey.hrContactFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.participantEmailPersonal,
        operatorIdKey:
          ProgramSearchProgramFormKey.participantEmailPersonalFilter,
      },
      {
        fieldName: ProgramSearchProgramFormKey.nisMobileISOCode,
      },
      {
        fieldName: ProgramSearchProgramFormKey.nisMobileCountryCode,
      },
      {
        fieldName: ProgramSearchProgramFormKey.nisParticipantMobileNumber,
      },
    ];

    const data: FilterProps[] = filters
      .filter(({ fieldName }) => isNotEmpty(values[fieldName].trim()))
      .map(({ fieldName, operatorIdKey }) => ({
        fieldName: fieldName as string,
        fieldValue: trimValue(values[fieldName]),
        operatorId: operatorIdKey ? values[operatorIdKey] : "",
        savedSearchFilterDataType: SAVED_SEARCH_PAGE_NAME.PROGRAM_SEARCH,
      }));

    if (preloadFilterValuesProgram?.id) {
      setPreloadFilterValuesProgram({
        id: preloadFilterValuesProgram.id,
        name: preloadFilterValuesProgram.name,
        filters: data,
      });
    } else {
      setFiltersData(data);
    }
  };

  const onSaveAsNewFilterClick = () => {
    setFiltersData([...preloadFilterValuesProgram.filters]);
    if (
      finalListOfFilters &&
      finalListOfFilters.length >= MAX_SAVE_FILTERS_LIMIT
    ) {
      setShowMultipleDeleteModal(true);
      setIsMaxFilterReached(true);
    } else {
      setPreloadFilter(false);
    }
  };

  const callbackAction = () => {
    if (isMaxFilterReached) {
      setPreloadFilter(false);
    }
    setIsOpenSaveFilterModal(true);
  };

  const filterListRefetchType = (searchTypeValue?: string) => {
    const refetchMap: Record<string, () => void> = {
      [searchTypeKey.individuals]: refetchListOfFiltersIndividuals,
      [searchTypeKey.programs]: refetchListOfFiltersProgram,
      [searchTypeKey.genericParticipant]:
        refetchListOfFiltersGenericParticipant,
      [searchTypeKey.nisParticipants]: refetchListOfFiltersNISParticipant,
    };

    const searchType = searchTypeValue ?? getValues().searchType;
    refetchMap[searchType]?.();
  };

  const getSavedFilterText = () => (
    <>
      <FilterListIcon /> {commonLabels.savedFilter}
    </>
  );

  const getResetAllText = () => (
    <>
      <ResetAllIcon /> {commonLabels.resetAllSearch}
    </>
  );

  const savedFilterMenuItems = [
    {
      onClick: handleSavedFilter,
      dataTestId: "saved-filter",
      label: <LabelWithIconText>{getSavedFilterText()}</LabelWithIconText>,
      disabled: isEmpty(finalListOfFilters),
    },
  ];

  const renderSearchButtonContainer = () => {
    const renderResetButton = () => {
      return (
        <MuiButtonTertiary
          data-testid={`${pageId}-clearAll`}
          onClick={() => resetFormValueBySearchType(false)}
        >
          <LabelWithIconText>{getResetAllText()}</LabelWithIconText>
        </MuiButtonTertiary>
      );
    };
    return isMobile ? (
      <MuiGrid className={`responsive-search-btn-container`}>
        {renderResetButton()}
        <MuiOptionMenu
          className="responsive-menu-item"
          menuItems={savedFilterMenuItems}
        />
      </MuiGrid>
    ) : (
      <MuiGrid className="form-header-right">
        {renderResetButton()}
        <MuiButtonTertiary
          onClick={handleSavedFilter}
          ref={anchorRef}
          data-testid={`${pageId}-saved-filter`}
        >
          <LabelWithIconText>{getSavedFilterText()}</LabelWithIconText>
        </MuiButtonTertiary>
      </MuiGrid>
    );
  };

  return (
    <ProgramSearchFormMainWrapper>
      <AccordionHeader
        title={<AccordionHeaderText label={commonLabels.search} />}
        isExpanded={!isMobile}
      >
        {renderSearchButtonContainer()}
        <Separator />
        <ProgramSearchForm
          onIndividualProgramRadioToggle={() =>
            resetFormValueBySearchType(true)
          }
          handleSaveFilter={handleSaveFilterModal}
          pageId={pageId}
        />
      </AccordionHeader>

      {isOpenSaveFilterModal && !preloadFilter && (
        <SaveFilterModal
          showModal={isOpenSaveFilterModal}
          handleModalClose={closeSaveFilterModal}
          pageId={pageId}
          savedSearchPageName={selectedSavedSearchPageName}
          filters={filtersData}
          listOfFilters={finalListOfFilters}
          refetchListOfFilters={filterListRefetchType}
        />
      )}

      {preloadFilter && isOpenSaveFilterModal && (
        <OverwriteFilterModal
          showModal={isOpenSaveFilterModal && preloadFilter}
          handleModalClose={closeSaveFilterModal}
          handleSaveNewFilterClick={onSaveAsNewFilterClick}
          overrideFilterValues={preloadFilterValuesProgram}
          refetchListOfFilters={filterListRefetchType}
        />
      )}

      {showSavedFilter &&
        finalListOfFilters &&
        (isMobile ? (
          <CommonListModal
            title={commonLabels.savedFilter}
            open={showSavedFilter}
            onIconButtonClick={handleDeleteClick}
            onFilterClick={handleFilterRowClickProgram}
            buttonIcon={<DeleteIcon />}
            listItems={finalListOfFilters}
            closeHandler={handleClose}
            secondaryButtonTitle={commonLabels.close}
          />
        ) : (
          <MuiMenuList
            menuArray={finalListOfFilters}
            refValue={anchorRef.current}
            open={showSavedFilter}
            onClose={handleClose}
            onRowClick={handleFilterRowClickProgram}
            onActionBtnClick={handleDeleteClick}
          />
        ))}
      {showModal && (
        <DeleteFilterModal
          showModal={showModal}
          handleModalClose={handleModalClose}
          handlePrimaryButtonClick={handlePrimaryButtonClick}
          filterToDelete={filterToDeleteProgram}
          refetchListOfFilters={filterListRefetchType}
        />
      )}

      {showMultipleDeleteModal && (
        <DeleteMultipleFilterModal
          showModal={showMultipleDeleteModal}
          handleModalClose={handleMultipleDeleteModalClose}
          handlePrimaryButtonClick={handleMultipleDeletePrimaryButtonClick}
          listOfFilters={finalListOfFilters}
          refetchListOfFilters={filterListRefetchType}
          callbackAction={callbackAction}
        />
      )}
    </ProgramSearchFormMainWrapper>
  );
};
